import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function PreviewPage() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const [error, setError] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState("");
  const selectedCameraIdRef = useRef("");
  const selectedMicrophoneIdRef = useRef("");
  const selectedSpeakerIdRef = useRef("");
  const [devices, setDevices] = useState({
    cameras: [],
    microphones: [],
    speakers: [],
  });

  const stopAudioMeter = useCallback(() => {
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  const stopPreviewResources = useCallback(() => {
    stopAudioMeter();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stopAudioMeter]);

  const refreshDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        return;
      }
      const available = await navigator.mediaDevices.enumerateDevices();
      const cameras = available.filter((d) => d.kind === "videoinput");
      const microphones = available.filter((d) => d.kind === "audioinput");
      const speakers = available.filter((d) => d.kind === "audiooutput");
      setDevices({ cameras, microphones, speakers });

      if (!selectedCameraIdRef.current && cameras.length > 0) {
        setSelectedCameraId(cameras[0].deviceId);
      }
      if (!selectedMicrophoneIdRef.current && microphones.length > 0) {
        setSelectedMicrophoneId(microphones[0].deviceId);
      }
      if (!selectedSpeakerIdRef.current && speakers.length > 0) {
        setSelectedSpeakerId(speakers[0].deviceId);
      }
    } catch (e) {
      // ignore device enumeration errors
    }
  }, []);

  const startAudioMeter = useCallback((stream) => {
    stopAudioMeter();
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const tick = () => {
      const activeAnalyser = analyserRef.current;
      if (!activeAnalyser) return;
      activeAnalyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i += 1) {
        const normalized = (data[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / data.length);
      setAudioLevel(Math.min(100, Math.round(rms * 220)));
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
  }, [stopAudioMeter]);

  const startPreview = useCallback(async () => {
    setError("");
    stopPreviewResources();

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser does not support camera/microphone testing.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedCameraId
          ? { deviceId: { exact: selectedCameraId } }
          : true,
        audio: selectedMicrophoneId
          ? { deviceId: { exact: selectedMicrophoneId } }
          : true,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (e) {
          // autoplay can fail without user gesture in some browsers
        }
      }

      setIsTesting(true);
      startAudioMeter(stream);
      await refreshDevices();
    } catch (e) {
      setIsTesting(false);
      setError(
        e?.message || "Unable to access camera/microphone. Check browser permissions."
      );
    }
  }, [
    refreshDevices,
    selectedCameraId,
    selectedMicrophoneId,
    startAudioMeter,
    stopPreviewResources,
  ]);

  const stopPreview = useCallback(() => {
    setIsTesting(false);
    stopPreviewResources();
  }, [stopPreviewResources]);

  useEffect(() => {
    selectedCameraIdRef.current = selectedCameraId;
  }, [selectedCameraId]);

  useEffect(() => {
    selectedMicrophoneIdRef.current = selectedMicrophoneId;
  }, [selectedMicrophoneId]);

  useEffect(() => {
    selectedSpeakerIdRef.current = selectedSpeakerId;
  }, [selectedSpeakerId]);

  useEffect(() => {
    refreshDevices();

    const onDeviceChange = () => {
      refreshDevices();
    };

    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
    }

    return () => {
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
      }
      stopPreviewResources();
    };
  }, [refreshDevices, stopPreviewResources]);

  useEffect(() => {
    const sinkId = selectedSpeakerId;
    const videoElement = videoRef.current;
    if (
      !sinkId ||
      !videoElement ||
      typeof videoElement.setSinkId !== "function"
    ) {
      return;
    }
    videoElement.setSinkId(sinkId).catch(() => {});
  }, [selectedSpeakerId, isTesting]);

  const meterColor = useMemo(() => {
    if (audioLevel >= 70) return "#2e7d32";
    if (audioLevel >= 35) return "#f9a825";
    return "#c62828";
  }, [audioLevel]);

  const fieldLabelStyle = {
    display: "grid",
    gap: "8px",
    minWidth: 0,
    color: "#1f2937",
    fontWeight: 500,
  };

  const selectStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    border: "1px solid #b8c2d4",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "14px",
    color: "#1f2937",
    background: "white",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f5f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 16px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "1020px",
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <img
            src="/rezRateLogo.png"
            alt="RezRate Logo"
            style={{ width: "132px", height: "auto", flexShrink: 0 }}
          />
          <div>
            <h1 style={{ margin: 0, lineHeight: 1.2, fontSize: "30px" }}>
              Rez Rate Meetings Test
            </h1>
            <p style={{ margin: "6px 0 0", color: "#4f5b67", fontSize: "16px" }}>
              Test camera and audio before joining any Rez Rate meeting. This preview will not be seen by other participants.
            </p>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gap: "14px",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            marginBottom: "14px",
          }}
        >
          <label style={fieldLabelStyle}>
            Camera
            <select
              value={selectedCameraId}
              onChange={(e) => setSelectedCameraId(e.target.value)}
              disabled={isTesting}
              style={selectStyle}
            >
              {devices.cameras.length === 0 && <option value="">No camera found</option>}
              {devices.cameras.map((device, idx) => (
                <option key={device.deviceId || `camera-${idx}`} value={device.deviceId}>
                  {device.label || `Camera ${idx + 1}`}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldLabelStyle}>
            Microphone
            <select
              value={selectedMicrophoneId}
              onChange={(e) => setSelectedMicrophoneId(e.target.value)}
              disabled={isTesting}
              style={selectStyle}
            >
              {devices.microphones.length === 0 && (
                <option value="">No microphone found</option>
              )}
              {devices.microphones.map((device, idx) => (
                <option key={device.deviceId || `mic-${idx}`} value={device.deviceId}>
                  {device.label || `Microphone ${idx + 1}`}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldLabelStyle}>
            Speaker (optional)
            <select
              value={selectedSpeakerId}
              onChange={(e) => setSelectedSpeakerId(e.target.value)}
              style={selectStyle}
            >
              {devices.speakers.length === 0 && <option value="">Default output</option>}
              {devices.speakers.map((device, idx) => (
                <option key={device.deviceId || `speaker-${idx}`} value={device.deviceId}>
                  {device.label || `Speaker ${idx + 1}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
          {!isTesting ? (
            <button
              type="button"
              onClick={startPreview}
              style={{
                background: "#1463ff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "11px 18px",
                fontSize: "15px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Start Device Test
            </button>
          ) : (
            <button
              type="button"
              onClick={stopPreview}
              style={{
                background: "#c62828",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "11px 18px",
                fontSize: "15px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Stop Test
            </button>
          )}
          <button
            type="button"
            onClick={refreshDevices}
            style={{
              background: "white",
              color: "#1463ff",
              border: "1px solid #1463ff",
              borderRadius: "8px",
              padding: "11px 18px",
              fontSize: "15px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Refresh Devices
          </button>
        </div>

        {error && (
          <div
            style={{
              background: "#fdecea",
              color: "#8a1f17",
              padding: "10px 12px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gap: "14px",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          }}
        >
          <div
            style={{
              border: "1px solid #d9dee8",
              borderRadius: "10px",
              padding: "14px",
            }}
          >
            <div style={{ marginBottom: "8px", fontWeight: 600 }}>Camera Preview</div>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                minHeight: "220px",
                background: "#111",
                borderRadius: "8px",
                objectFit: "cover",
                transform: "scaleX(-1)",
              }}
            />
          </div>

          <div
            style={{
              border: "1px solid #d9dee8",
              borderRadius: "10px",
              padding: "14px",
            }}
          >
            <div style={{ marginBottom: "8px", fontWeight: 600 }}>Microphone Level</div>
            <div
              style={{
                height: "18px",
                background: "#edf1f7",
                borderRadius: "999px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${audioLevel}%`,
                  height: "100%",
                  background: meterColor,
                  transition: "width 60ms linear",
                }}
              />
            </div>
            <p style={{ marginBottom: 0, color: "#4f5b67" }}>
              {isTesting
                ? "Speak and check if the bar moves."
                : "Start the test to activate microphone meter."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreviewPage;
